import React, { useEffect, useRef, useState } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import TabPanel from "@mui/lab/TabPanel";
import { TabContext } from "@mui/lab";
import { TextField } from "@mui/material";

import "./Section.css";
import placeholderImage from "../assets/placeholder-image.png"; // Added import
import SuccessAlert from "../components/success-alert/SuccessAlert";
import ErrorAlert from "../components/error-alert/ErrorAlert";
import useLanguages from "../hooks/useLanguages";
import { API, SERVER_DOMAIN } from "../assets/staticData";
import TextEditor from "../components/rich-text/TextEditor/TextEditor";
import ClearCacheButton from "../components/ClearCacheButton/ClearCacheButton";
import { CACHE_KEY } from "../constant/cache-key";

export default function Banner({ user }) {
  const langs = useLanguages();

  const [value, setValue] = useState("0");
  const [data, setData] = useState([]);
  const [bannerImages, setBannerImages] = useState([]); // State for new file selections
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [fail, setFail] = useState(false);
  const [loading, setLoading] = useState(true);

  const hiddenFileInput = useRef(); // Ref for the hidden file input
  let userToken = localStorage.getItem("userToken");

  const handleChange = (event, newValue) => {
    setValue(newValue);
  };

  let tabStyle = { textTransform: "capitalize", fontWeight: "500" };

  useEffect(() => {
    // Initialize structures for all languages
    let docs = langs.map((item) => ({
      slogan: "",
      desc: "",
      btnT: "",
      btnL: "",
      image: null, // Ensure 'image' property exists
      language: item.attributes.language,
    }));
    setData(docs);

    setErrors(langs.map(() => ({ error: false })));
    setBannerImages(langs.map(() => ({ file: null })));

    async function getData() {
      try {
        const res = await fetch(`${API}/hero-banner?populate=*`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userToken}`,
          },
        });
        const response = await res.json();
        const apiData = response.data?.attributes?.bannerData || [];

        setData((prevData) => {
          return prevData.map((localItem) => {
            const apiItem = apiData.find(
              (apiItem) => apiItem.language === localItem.language
            );

            if (apiItem) {
              return {
                ...localItem,
                slogan: apiItem.slogan || "",
                desc: apiItem.desc || "",
                btnT: apiItem.btnT || "",
                btnL: apiItem.btnL || "",
                image: apiItem.image || null,
              };
            }
            return localItem;
          });
        });
        setLoading(false);
      } catch (err) {
        console.error("Fetch error:", err);
      }
    }

    getData();
  }, [langs]);

  const handleImageChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      setBannerImages((prev) =>
        prev.map((item, idx) => (idx === index ? { file: file } : item))
      );
    }
  };

  const submitData = async (item, index) => {
    let finalData = [...data];

    // 1. If there's a new image, upload it first
    if (bannerImages[index].file) {
      const formData = new FormData();
      formData.append("files", bannerImages[index].file);

      try {
        const uploadRes = await fetch(`${API}/upload`, {
          method: "POST",
          headers: { Authorization: `Bearer ${userToken}` },
          body: formData,
        });
        const uploadData = await uploadRes.json();

        // Update the image URL for this specific language tab
        finalData[index].image = uploadData[0].url;
      } catch (err) {
        setFail(true);
        return;
      }
    }

    // 2. Submit the JSON data (with the new image URL if applicable)
    fetch(`${API}/hero-banner`, {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${userToken}`,
      },
      body: JSON.stringify({
        data: { bannerData: finalData },
      }),
    }).then((res) => {
      if (res.ok) {
        setSuccess(true);
        setData(finalData); // Sync local state
      } else {
        setFail(true);
      }
    });
  };

  return (
    <TabContext value={value}>
      <Box sx={{ width: "100%" }}>
        <h3 style={{ textTransform: "capitalize" }}>Banner Settings</h3>

        <Box mt={2}>
          <ClearCacheButton
            keys={[
              CACHE_KEY.heroBanner
            ]} />
        </Box>
        <Tabs
          value={value}
          onChange={handleChange}
          textColor="secondary"
          indicatorColor="secondary"
        >
          {langs?.map((item, index) => (
            <Tab
              key={index}
              sx={tabStyle}
              value={index.toString()}
              label={item.attributes.language}
              icon={
                <img
                  src={SERVER_DOMAIN + item.attributes.icon.data.attributes.url}
                  style={{ width: 25, aspectRatio: 1, borderRadius: 20 }}
                  alt="lang-icon"
                />
              }
              iconPosition="start"
            />
          ))}
        </Tabs>

        {!loading && (
          <>
            {langs?.map((item, index) => (
              <TabPanel value={index.toString()} key={index}>
                <SuccessAlert show={success} setShow={(v) => setSuccess(v)} />
                <ErrorAlert show={fail} setShow={(v) => setFail(v)} />

                <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
                  {/* Slogan & Description Inputs */}
                  <div>
                    <p style={{ fontSize: 15, marginBottom: 8 }}>Slogan</p>
                    <TextField
                      sx={{ width: 500 }}
                      variant="outlined"
                      value={data[index]?.slogan || ""}
                      onChange={(e) => {
                        const newData = [...data];
                        newData[index].slogan = e.target.value;
                        setData(newData);
                      }}
                    />
                  </div>

                  <div>
                    <p style={{ fontSize: 15, marginBottom: 8 }}>Description</p>
                    <TextEditor
                      value={data[index]?.desc || ""}
                      onChange={(v) => {
                        const newData = [...data];
                        newData[index].desc = v;
                        setData(newData);
                      }}
                    />
                  </div>

                  {/* Buttons Inputs */}
                  <div>
                    <p style={{ fontSize: 15, marginBottom: 8 }}>Action Button</p>
                    <TextField
                      sx={{ width: 300 }}
                      label="Text"
                      value={data[index]?.btnT || ""}
                      onChange={(e) => {
                        const newData = [...data];
                        newData[index].btnT = e.target.value;
                        setData(newData);
                      }}
                    />
                    <TextField
                      sx={{ width: 300, ml: 2 }}
                      label="Link"
                      value={data[index]?.btnL || ""}
                      onChange={(e) => {
                        const newData = [...data];
                        newData[index].btnL = e.target.value;
                        setData(newData);
                      }}
                    />
                  </div>

                  {/* Image Uploader */}
                  <div>
                    <p style={{ fontSize: 15, marginBottom: 8 }}>Banner Image</p>
                    <div
                      className="placeholderIMG"
                      style={{ cursor: "pointer", width: "fit-content" }}
                      onClick={() => hiddenFileInput.current.click()}
                    >
                      <img
                        style={{
                          width: 400,
                          height: 200,
                          objectFit: "cover",
                          borderRadius: 8,
                          border: "1px solid #ddd"
                        }}
                        src={
                          bannerImages[index]?.file
                            ? URL.createObjectURL(bannerImages[index].file)
                            : data[index]?.image
                              ? SERVER_DOMAIN + data[index].image
                              : placeholderImage
                        }
                        alt="Banner Preview"
                      />
                    </div>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleImageChange(e, index)}
                      ref={hiddenFileInput}
                      style={{ display: "none" }}
                    />
                  </div>
                </div>

                {user.perms[7].edit && (
                  <div className="submitBTN" onClick={() => submitData(item, index)}>
                    Save Changes
                  </div>
                )}
              </TabPanel>
            ))}
          </>
        )}
      </Box>
    </TabContext>
  );
}